// src/components/xgrid-helpers.tsx
import { CSSProperties } from "react";
import { IconButton, Tooltip, Typography } from "@material-ui/core";
import { GridCellParams, GridValueFormatterParams } from "@mui/x-data-grid";
import { LicenseInfo } from '@mui/x-license';
import { isEmpty, isNull, isObject, isUndefined } from "lodash";
import { FormatterArgs } from "app-types";
import StatusChip from "components/status-chip";
import { downloadResource } from "helpers/download-helpers";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import GetAppIcon from "@material-ui/icons/GetApp";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import MailOutlineIcon from "@material-ui/icons/MailOutline";

const fontStyle: CSSProperties = {
  fontSize: "inherit",
  fontWeight: "inherit",
  fontFamily: "inherit",
};



//---
//Extracts a field value out of a json structure
//EXAMPLE:
//{
//   "formatter": "json",
//   "formatterArgs": "renderParametersJson",
//   "field": "recipientEmail",
//   "headerName": "Sent To",
// },
export const jsonFormatter = (args: FormatterArgs) => (params: GridCellParams) => {
  const row = params.row;
  
  const jsonProp = isObject(args) ? args.jsonProp : 'renderParametersJson';
  const value = row[jsonProp]?.[params.colDef.field];
  
  return value ?? '';  // Return the value, or an empty string if undefined
};


//---
//Extracts multiple field value out of a json structure, and combines them with spaces between
//EXAMPLE:
// {
//   "formatter": "jsonCombine",
//   "formatterArgs": {
//     "jsonProp": "renderParametersJson",
//     "separator": ", "
//   },
//   // or "formatterArgs": "renderParametersJson" for default separator of space
//   "field": "lastName, firstName",
//   "headerName": "Recipient",
// },
export const jsonCombineFormatter = (args: FormatterArgs) => (params: GridCellParams) => {
  const row = params.row;

  const jsonProp = isObject(args) ? args.jsonProp : 'renderParametersJson';
  const separator = isObject(args) ? args.separator || " " : " ";
  const fields = params.colDef.field.split(",") as string[];

  const fieldValues = fields.map((f) => row[jsonProp]?.[f.trim()]);
  const value = fieldValues.filter(Boolean).join(separator);

  return value;
};


//---
//takes the first non-null, non-blank value out of multiple field values
//EXAMPLE:
// {
//   "formatter": "coalesce",
//   "field": "renderParametersJson.recipientEmail, userEmail",
//   "headerName": "Recipient Email",
// }
export const coalesceFormatter = (params: GridCellParams) => {
  const row = params.row;

  const fields = params.colDef.field.split(",") as string[];
  const fieldValues = fields.map((f) => {
    if (f.includes(".")) {
      const [mainProp, subProp] = f.split(".");
      return row[mainProp.trim()]?.[subProp.trim()];
    } else {
      return row[f.trim()];
    }
  });

  const value = fieldValues.find((v) => !isNull(v) && !isUndefined(v) && !isEmpty(v));

  return value;
};


export const LinkCell = (args: any) => (params: any) => {
  const { label, isDownload, isMail, icon } = args ?? {};
  const val = params.colDef.valueFormatter ? params.colDef.valueFormatter(params) : params.value;

  const url = isMail ? `mailto://${val}` : (!val || val.indexOf("http") === 0) ? val : `http://${val}`;
  if (!val) return <span />;

  //So this doesn't trigger the row selection.
  //For downloads we also can't rely on the anchor's download attribute — it is ignored
  //for cross-origin URLs (our media origin), which made the browser play the mp4
  //instead. Fetch to a blob and save it, like the detail view does.
  const onClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.stopPropagation();

    if (isDownload && url) {
      e.preventDefault();
      downloadResource(url).catch(() => {
        window.open(url, "_blank", "noopener,noreferrer");
      });
    }
  }

  // Determine which icon to display
  const getIcon = () => {
    if (icon === 'play') return <PlayArrowIcon fontSize="small" />;
    if (icon === 'download' || isDownload) return <GetAppIcon fontSize="small" />;
    if (icon === 'mail' || isMail) return <MailOutlineIcon fontSize="small" />;
    if (icon === 'open') return <OpenInNewIcon fontSize="small" />;
    return null;
  };

  const iconElement = getIcon();
  const displayLabel = label || val;
  const title = `click to open: ${val}`;

  // If icon is specified, render as icon button
  if (iconElement) {
    return (
      <Tooltip title={title}>
        <IconButton
          size="small"
          href={url}
          target="_blank"
          download={Boolean(isDownload)}
          onClick={onClick}
          color="primary"
        >
          {iconElement}
        </IconButton>
      </Tooltip>
    );
  }

  // Otherwise render as text link
  return (
    <a href={url} target="_blank" title={title} download={Boolean(isDownload)} onClick={onClick}>
      {displayLabel}
    </a>
  );
};

export const myValueFormatter = (params: GridValueFormatterParams) => {
  const v = params.value;
  return v ? String(v) : '';
};

export const TipCell = (args: any) => (params: GridCellParams) => {
  const displayValue = params.value !== undefined && params.value !== null ? String(params.value) : "";

  return (
    <Tooltip title={displayValue}>
      <Typography style={fontStyle}>{displayValue}</Typography>
    </Tooltip>
  );
};

//= ({ status } : {status: string})
export const ReportStatus = (args: any) => (params: any) => {
  return <StatusChip status={params.value} />
};